import { useEffect, useMemo, useSyncExternalStore } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { CLAIMS_APP_VERSION } from "../../lib/appVersion";
import {
  buildReportUser,
  getSeqReportModuleOverride,
  pushSeqReportContext,
  resolveBuildId,
  resolveSeqReportModule,
  startSeqReportSession,
  subscribeSeqReportModuleOverride,
} from "../../lib/seqReport";
import { startMoneySweep } from "../../lib/seqReportPrivacyDom";

// SEQ-192: the SEQ "Report a bug / Request a feature" widget.
//
// Mounted inside the signed-in layout only (AppLayout, after the auth and
// access guards), so the login, setup and access-restricted screens never load
// it. Renders nothing itself.
//
// ORDERING: do not ship this to production before PR #3 (feat/seq193-mom-table,
// commit e82d6c0) merges. Claims production still runs Sentry replay with
// maskAllText: false (src/main.tsx), and a report screenshot plus replay text
// must not both carry unmasked figures. The dollar figures are also marked
// data-seq-private here (lib/seqReportPrivacyDom.ts) so the screenshot blur
// hides them either way.
//
// The report service also needs this app's origin on its allowlist before it
// accepts a report (see docs/qa/seq192-server-step.md).
export function SeqReportWidget() {
  const { user, profile, session, orgRole } = useAuth();
  const { pathname } = useLocation();
  const override = useSyncExternalStore(
    subscribeSeqReportModuleOverride,
    getSeqReportModuleOverride,
    () => null,
  );
  const module = resolveSeqReportModule(pathname, override);

  const userId = user?.id ?? "";
  const userEmail = profile?.email ?? user?.email ?? "";
  const userName = profile?.full_name ?? null;
  const userRole = orgRole ?? "member";
  const token = session?.access_token ?? "";

  const reportUser = useMemo(
    () =>
      userId
        ? buildReportUser({ id: userId, email: userEmail, fullName: userName, role: userRole })
        : null,
    [userId, userEmail, userName, userRole],
  );

  // Load the widget once, and keep dollar figures marked while it is here.
  // On sign out the layout unmounts: hide the button and drop the token so a
  // report can never go out from the login screen.
  useEffect(() => {
    const build = resolveBuildId({
      commit: __BUILD_COMMIT__,
      version: import.meta.env.VITE_APP_VERSION,
      fallback: CLAIMS_APP_VERSION,
    });
    return startSeqReportSession({
      win: window,
      doc: document,
      build,
      startSweep: () => startMoneySweep(document.body),
    });
  }, []);

  // Context on mount, on every route change and on token refresh. The route
  // itself (path, query values stripped) is read by the widget from the
  // location; module is the part the app has to say.
  useEffect(() => {
    if (!reportUser || !token) return;
    return pushSeqReportContext(window, { user: reportUser, token, module });
  }, [reportUser, token, module]);

  return null;
}
